import { useEffect, useMemo, useState } from 'react';
import {
  Clock,
  LineChart as LineChartIcon,
  PhoneCall,
  RefreshCw,
  Server,
  Table as TableIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import { useLatestServers } from '../api';
import { AllServersRangeTab } from '../components/AllServersRangeTab';
import { DialerCallsTab } from '../components/DialerCallsTab';
import { LatestStatusTab } from '../components/LatestStatusTab';
import { ServerHistoryTab } from '../components/ServerHistoryTab';
import type { StatusFilter } from '../types';

type TabId = 'latest' | 'calls' | 'history' | 'all';

const TABS: { id: TabId; label: string; icon: typeof Server }[] = [
  { id: 'latest', label: 'Latest Status', icon: Server },
  { id: 'calls', label: 'Dialer Calls', icon: PhoneCall },
  { id: 'history', label: 'Server History', icon: LineChartIcon },
  { id: 'all', label: 'All Servers Range', icon: TableIcon },
];

function formatHMS(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export function MonitoringPage() {
  const [activeTab, setActiveTab] = useState<TabId>('latest');
  const [filter, setFilter] = useState<StatusFilter>('all');
  // Track the wall-clock of the most recent successful refresh so the
  // header banner shows when the data is from.
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const latest = useLatestServers();
  const servers = latest.data ?? [];

  useEffect(() => {
    if (latest.dataUpdatedAt) {
      setLastUpdated(new Date(latest.dataUpdatedAt));
    }
  }, [latest.dataUpdatedAt]);

  const updatedStr = useMemo(
    () => (lastUpdated ? formatHMS(lastUpdated) : ''),
    [lastUpdated],
  );

  return (
    <FillPage>
      <PageHeader
        title="Health"
        description="Live status of every server reporting metrics — auto-refreshes every minute."
        actions={
          <div className="flex items-center gap-2">
            {updatedStr && (
              <span className="flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 font-mono text-[11px] text-muted-foreground">
                <Clock className="h-3 w-3" />
                {updatedStr}
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => latest.refetch()}
              disabled={latest.isFetching}
            >
              <RefreshCw
                className={cn(
                  'h-3.5 w-3.5',
                  latest.isFetching && 'animate-spin',
                )}
              />
              {latest.isFetching ? 'Refreshing' : 'Refresh'}
            </Button>
          </div>
        }
      />

      {/* Tab bar */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-border">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={cn(
                'relative flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors',
                isActive
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
              <span
                aria-hidden
                className={cn(
                  'absolute inset-x-2 bottom-0 h-[2px] rounded-full transition-colors',
                  isActive ? 'bg-primary' : 'bg-transparent',
                )}
              />
            </button>
          );
        })}
      </div>

      {/* Tab body — each tab owns its own scroll */}
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {activeTab === 'latest' && (
          <LatestStatusTab
            servers={servers}
            isLoading={latest.isLoading}
            activeFilter={filter}
            onFilterChange={setFilter}
          />
        )}
        {activeTab === 'calls' && (
          <DialerCallsTab servers={servers} isLoading={latest.isLoading} />
        )}
        {activeTab === 'history' && (
          <ServerHistoryTab
            servers={servers}
            defaultServerName={servers[0]?.serverName}
          />
        )}
        {activeTab === 'all' && <AllServersRangeTab />}
      </div>
    </FillPage>
  );
}
